import { useCallback, useEffect, useMemo, useState } from 'react'

import { imageUrl } from './constants.ts'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
const FALLBACK_PATHS = ['img/token.png', 'img/token.svg']

const isFallbackPath = (value: string) => {
  const normalized = value.replace(/^\/+/, '').toLowerCase()
  return FALLBACK_PATHS.includes(normalized)
}

type LogoArgs = {
  address?: string
  chainId?: number | string
  version?: string
  initialUrl?: string
}

const normalizeInitialUrl = (value?: string) => {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (isFallbackPath(trimmed)) return undefined
  if (trimmed.startsWith('http') || trimmed.startsWith('data:')) return trimmed
  if (trimmed.startsWith('/')) return trimmed
  return `/${trimmed.replace(/^\/+/, '')}`
}

const dedupePush = (list: string[], candidate?: string) => {
  if (!candidate) return
  if (!list.includes(candidate)) list.push(candidate)
}

const addressVariants = (address?: string) => {
  if (!address) return []
  const variants = new Set<string>()
  variants.add(address)
  variants.add(address.toLowerCase())
  variants.add(address.toUpperCase())
  return Array.from(variants).filter(Boolean)
}

const chainVariants = (chainId?: number | string) => {
  if (chainId === undefined || chainId === null || chainId === '') return []
  const variants = new Set<string>()
  const raw = String(chainId)
  variants.add(raw)
  const numeric = Number(chainId)
  if (!Number.isNaN(numeric)) variants.add(String(numeric))
  return Array.from(variants)
}

export const buildTokenLogoCandidates = ({
  address,
  chainId,
  version,
  initialUrl
}: LogoArgs = {}): string[] => {
  const candidates: string[] = []
  dedupePush(candidates, normalizeInitialUrl(initialUrl))

  const addrList = addressVariants(address).filter(
    a => a && a !== ZERO_ADDRESS
  )
  const chainList = chainVariants(chainId)
  const isLegacy = (version ?? '').toLowerCase() === 'v2'

  for (const addr of addrList) {
    if (chainList.length) {
      for (const cid of chainList) {
        dedupePush(candidates, `${imageUrl}${addr}-${cid}-logo.png`)
        dedupePush(candidates, `${imageUrl}${addr}-${cid}.png`)
      }
    }

    dedupePush(candidates, `${imageUrl}${addr}-logo.png`)
    if (isLegacy) {
      dedupePush(candidates, `${imageUrl}${addr}_logo.png`)
    }
    dedupePush(candidates, `${imageUrl}${addr}.png`)
  }

  return candidates
}

export const useTokenLogo = (args: LogoArgs) => {
  const candidates = useMemo(() => buildTokenLogoCandidates(args), [
    args.address,
    args.chainId,
    args.version,
    args.initialUrl
  ])

  const [index, setIndex] = useState(0)

  useEffect(() => {
    setIndex(0)
  }, [candidates])

  const onError = useCallback(() => {
    setIndex(i => (i + 1 < candidates.length ? i + 1 : i))
  }, [candidates])

  const logoSrc = candidates[index]

  return { logoSrc, onError, candidates }
}
